import { useQuery } from "@tanstack/react-query";

import { isSignedContainerFile } from "../constants/fileTypes";
import type { EnclosedDocument, SignedContainerInfo } from "./signatureModel";
import { fetchEnclosedDocuments, fetchSignedContainerInfo } from "./signedContainers";

/** How a row relates to the container: it is one, or it sits inside one. */
export type SignedContainerRole = "container" | "enclosed";

export interface SignedRowInfo {
  info: SignedContainerInfo;
  role: SignedContainerRole;
}

/** The shape every view's row model already provides (list rows carry string
 *  ids, the timeline's `DocumentRowMapped` carries numeric ones). */
export interface SignedContainerCandidate {
  id: string | number;
  filename: string;
  parent_document_id?: string | number | null;
  /**
   * The parent's filename, when the caller already has it (the list rows load
   * the parent from the MST store for the "parent filename" column).
   * `parent_document_id` is the generic parent-child column — DWG child PDFs
   * and IFC derivatives carry it too — so a known non-container parent name
   * lets the hook skip a lookup that could only resolve to `null`. Leave
   * `undefined` when unknown and the hook resolves it by querying.
   */
  parentFilename?: string;
}

/**
 * Container documents are immutable once signed (a re-sign produces a new
 * document), so both queries can be cached indefinitely. That matters: rows are
 * virtualised and every enclosed document of the same container asks the same
 * question, so a 50-row page would otherwise fire the same query dozens of times.
 */
const CONTAINER_QUERY_OPTIONS = { staleTime: Infinity, retry: false } as const;

const infoKey = (containerId: string | null) => ["signed-container-info", containerId] as const;
const enclosedKey = (containerId: string | null) =>
  ["signed-container-enclosed", containerId] as const;

/**
 * Resolve the signed-container context of a single document row — used
 * identically by the Folders row, the List row and the Timeline node so the
 * seal, the badge and the panel stay consistent across views.
 *
 * Returns `null` for anything unrelated to signing: a regular file, a plain
 * `.zip`, or a document inside a plain `.zip`. A failed lookup degrades to the
 * same `null` — a signature query must never take a document row down with it.
 */
export function useSignedContainerInfo(doc: SignedContainerCandidate): SignedRowInfo | null {
  const docId = String(doc.id);
  const containerId = resolveContainerId(doc);

  const { data: info } = useSignedContainerInfoQuery(containerId);

  if (!info) return null;
  return { info, role: info.containerId === docId ? "container" : "enclosed" };
}

/**
 * Which document id (if any) is worth asking the container question about:
 * the row itself when it IS a signed container, its parent when the parent
 * might be one, `null` when no query could ever return info — a known
 * non-container parent (DWG child PDF, IFC derivative) never becomes signed.
 */
export function resolveContainerId(doc: SignedContainerCandidate): string | null {
  if (isSignedContainerFile(doc.filename)) return String(doc.id);
  if (doc.parent_document_id == null) return null;
  if (doc.parentFilename !== undefined && !isSignedContainerFile(doc.parentFilename)) return null;
  return String(doc.parent_document_id);
}

/** Signer/validity metadata for one container. */
export function useSignedContainerInfoQuery(containerId: string | null) {
  return useQuery({
    queryKey: infoKey(containerId),
    queryFn: () => (containerId ? fetchSignedContainerInfo(containerId) : null),
    enabled: containerId !== null,
    ...CONTAINER_QUERY_OPTIONS,
  });
}

/** The documents sealed inside one container. `enabled` defers the fetch until
 *  something actually reveals them (the row's dropdown, the open panel). */
export function useEnclosedDocumentsQuery(containerId: string | null, enabled = true) {
  return useQuery<EnclosedDocument[]>({
    queryKey: enclosedKey(containerId),
    queryFn: () => (containerId ? fetchEnclosedDocuments(containerId) : []),
    enabled: enabled && containerId !== null,
    ...CONTAINER_QUERY_OPTIONS,
  });
}
